/**
 * The picker's two row shapes, shared by the comparison-source modal and by
 * PlayerSegmentLibrary.
 *
 * In their own module rather than exported from the modal, because the modal
 * imports PlayerSegmentLibrary and PlayerSegmentLibrary would then import the
 * modal back.
 */

import { EBTN_ICON } from './editorButtonStyles';
import { usePhoneLayout } from './usePhoneLayout';

/**
 * One row in the source list. Deliberately identical to SideBySidePopover's
 * rows in the organizer rail — same padding, same 12px label, same 16px
 * biscayne icon, same hover wash — because the rail and this modal are now
 * asking the same question and should not look like two different questions.
 */
const LIST_ROW =
  'w-full px-3 py-2.5 text-left text-xs flex items-center gap-2.5 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';

export function SourceListRow({
  label, icon, available, note, onOpen,
}: {
  label: string;
  icon: React.ReactNode;
  available: boolean;
  note?: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={available ? onOpen : undefined}
      disabled={!available}
      className={`${LIST_ROW} ${
        available
          ? 'text-[var(--editor-text)] hover:bg-[var(--editor-hover)] cursor-pointer'
          : 'text-[var(--editor-text-muted)] cursor-default'
      }`}
    >
      <span className={`w-4 h-4 flex-shrink-0 ${available ? 'text-biscayne' : 'text-[var(--editor-text-muted)]'}`}>
        {icon}
      </span>
      {label}
      {!available && note && <span className="ml-auto text-[10px]">{note}</span>}
    </button>
  );
}

/**
 * A browsable item: one clear primary line, one quieter secondary line.
 *
 * The row selects; preview is a sibling button, not a nested one. Nesting a
 * button inside a button is invalid and would have put the two hit areas on
 * top of each other, so the selectable area stops where the preview begins.
 */
/**
 * One transition, used by everything that changes when a row is selected.
 *
 * The values are the ones `transition-colors` already resolves to, written out
 * rather than inherited from Tailwind's defaults so a change to those defaults
 * cannot quietly retune this.
 *
 * It exists because the row used to animate in three speeds at once: the
 * container eased its background and border over 150ms, while the title colour
 * and the check mark had no transition at all and snapped. Identical in every
 * list — they all render this component — but the mix reads differently
 * depending on how much of a row is title, which is why some lists looked
 * wrong next to others.
 */
const SELECT_TRANSITION =
  'transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]';

export function BrowseRow({
  primary, secondary, thumb, selected = false, onSelect, onTogglePreview, previewing, children,
}: {
  primary: string;
  secondary?: string;
  /** Leading thumbnail slot. Fixed size, so the row does not reflow. */
  thumb?: React.ReactNode;
  /** Selecting no longer commits, so the row has to show what is chosen. */
  selected?: boolean;
  onSelect: () => void;
  onTogglePreview?: () => void;
  previewing?: boolean;
  children?: React.ReactNode;
}) {
  /* Phone (spec 14.4): a row of an iOS inset grouped list (the .lve-group it
     sits in draws the group and the separators): no border or card of its
     own, 60 high, title Body 17/22, subtitle Subhead 15/20 in grey, a light
     wash while pressed; selection is the check and the title's colour. */
  const phone = usePhoneLayout().phone;
  return (
    <div
      className={phone ? 'overflow-hidden' : `rounded-lg overflow-hidden border ${SELECT_TRANSITION}`}
      style={phone ? undefined : {
        background: selected ? 'var(--editor-selected)' : 'var(--editor-surface)',
        borderColor: selected ? 'var(--editor-selected-border)' : 'var(--editor-border)',
      }}
    >
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          className={phone
            ? 'flex-1 min-w-0 text-left px-4 min-h-[60px] cursor-pointer flex items-center gap-3 active:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40'
            : 'ios-press flex-1 min-w-0 text-left px-3 py-2 cursor-pointer flex items-center gap-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40'}
        >
          {thumb}
          <span className="flex-1 min-w-0">
            <span
              className={`block [overflow-wrap:anywhere] ${phone ? 'text-[17px] leading-[22px] font-normal' : 'text-sm font-medium'} ${SELECT_TRANSITION}`}
              style={{ color: selected ? 'var(--editor-selected-text)' : 'var(--editor-text)' }}
            >
              {primary}
            </span>
            {secondary && (
              <span className={`block [overflow-wrap:anywhere] text-[var(--editor-text-muted)] ${phone ? 'text-[15px] leading-[20px]' : 'text-xs'}`}>{secondary}</span>
            )}
          </span>
          {/* Always rendered, faded rather than mounted. A conditionally
              mounted element has nothing to transition from, so the check used
              to pop in — and it pushed the text sideways as it did, an
              unanimated shift on every selection. Holding the slot costs 16px
              of width on unselected rows and removes the jump. */}
          <svg
            className={`w-4 h-4 flex-shrink-0 transition-opacity duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] ${selected ? 'opacity-100' : 'opacity-0'}`}
            style={{ color: 'var(--editor-selected-text)' }}
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
          </svg>
        </button>
        {onTogglePreview && (
          <button
            type="button"
            onClick={onTogglePreview}
            className={`${EBTN_ICON} self-center mr-2`}
            title={previewing ? 'Close preview' : 'Preview'}
            aria-label={previewing ? 'Close preview' : 'Preview'}
          >
            {previewing
              ? <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              : <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

